<div class="media-import">
  <p class="text-sm text-600 mt-0 mb-3 media-hint" data-clarity-unmask="true">
    Paczka mediów z ministerstwa to plik ZIP z filmami (.wmv) i zdjęciami (.jpg) do pytań egzaminacyjnych.
    Pliki trafiają do katalogu mediów serwera i są od razu dodawane do indeksu, bez restartu.
    Plik o znanej już nazwie jest porównywany z obecnym: identyczny zostaje bez zmian, zmieniony jest
    podmieniany dopiero po sprawdzeniu sumy kontrolnej, a nowe filmy .wmv idą do konwersji.
    Puste pliki, pliki systemowe i inne rozszerzenia są pomijane i wymienione w raporcie.
  </p>

  @if (error(); as message) {
    <div class="error-banner" role="alert">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ message }}</span>
    </div>
  }

  <div class="upload-card">
    <input
      #fileInput
      type="file"
      accept=".zip,application/zip,application/x-zip-compressed"
      class="hidden-input"
      (change)="onFileSelected($event)"
      data-testid="media-file-input"
    />
    <div class="upload-row">
      <p-button
        label="Wybierz plik ZIP"
        icon="pi pi-folder-open"
        [outlined]="true"
        [disabled]="uploading() || isRunning"
        (onClick)="fileInput.click()"
        data-clarity-unmask="true"
      />
      @if (selectedFile(); as file) {
        <span class="selected-file" data-clarity-unmask="true">
          <i class="pi pi-file"></i>
          <span class="selected-file__name">{{ file.name }}</span>
          <span class="text-600">({{ formatBytes(file.size) }})</span>
          @if (!uploading()) {
            <p-button icon="pi pi-times" [text]="true" [rounded]="true" size="small" severity="secondary"
              pTooltip="Usuń wybór" (onClick)="clearFile()" data-clarity-unmask="true" />
          }
        </span>
      } @else {
        <span class="text-600 text-sm">Nie wybrano pliku.</span>
      }
      <span class="flex-grow-1"></span>
      @if (uploading()) {
        <p-button label="Przerwij" icon="pi pi-times" severity="danger" [outlined]="true" (onClick)="cancelUpload()" data-clarity-unmask="true" />
      } @else {
        <p-button
          label="Wyślij i zaimportuj"
          icon="pi pi-upload"
          [disabled]="!canUpload"
          [pTooltip]="isRunning ? 'Poczekaj, aż bieżący import się zakończy' : ''"
          tooltipPosition="left"
          (onClick)="upload()"
          data-clarity-unmask="true"
        />
      }
    </div>

    @if (uploading()) {
      <div class="upload-progress">
        <div class="flex justify-content-between text-sm mb-1">
          <span>Przesyłanie paczki na serwer…</span>
          <span class="font-semibold">{{ uploadProgress() ?? 0 }}% · {{ formatBytes(uploadedBytes()) }} z {{ formatBytes(selectedFile()?.size) }}</span>
        </div>
        <p-progressBar [value]="uploadProgress() ?? 0" [showValue]="false" styleClass="media-progress"></p-progressBar>
        <p class="text-xs text-600 mt-1 mb-0">Nie zamykaj tej karty do końca przesyłania. Rozpakowywanie zacznie się po odebraniu całego pliku.</p>
      </div>
    }
  </div>

  <div class="status-card">
    <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <div class="flex align-items-center gap-3 flex-wrap">
        <span class="font-semibold text-lg" data-clarity-unmask="true">Ostatni import</span>
        <span class="state-badge" [ngClass]="stateClass(status())" data-clarity-unmask="true">
          <i class="pi" [ngClass]="isRunning ? 'pi-spin pi-spinner' : status()?.state === 'Failed' ? 'pi-times-circle' : status()?.state === 'Completed' ? 'pi-check-circle' : 'pi-info-circle'"></i>
          {{ stateLabel(status()) }}
        </span>
      </div>
      <p-button label="Odśwież" icon="pi pi-refresh" [outlined]="true" size="small" [loading]="loadingStatus()" (onClick)="loadStatus()" data-clarity-unmask="true" />
    </div>

    @if (status(); as s) {
      @if (s.state !== 'Idle') {
        <div class="meta-row text-sm">
          <span><span class="text-600">Plik:</span> {{ s.fileName }} ({{ formatBytes(s.fileSizeBytes) }})</span>
          <span><span class="text-600">Start:</span> {{ formatDateTime(s.startedAt) }}</span>
          @if (s.finishedAt) {
            <span><span class="text-600">Koniec:</span> {{ formatDateTime(s.finishedAt) }}</span>
          }
        </div>

        <div class="progress-section">
          <div class="flex justify-content-between text-sm mb-1">
            <span>{{ s.processedEntries }} z {{ s.totalEntries }} plików</span>
            <span class="font-semibold">{{ s.progressPercentage }}%</span>
          </div>
          <p-progressBar [value]="s.progressPercentage" [showValue]="false" [mode]="s.state === 'Indexing' ? 'indeterminate' : 'determinate'" styleClass="media-progress"></p-progressBar>
          @if (s.currentEntry) {
            <div class="text-xs text-600 mt-1 current-entry">{{ s.currentEntry }}</div>
          }
        </div>

        @if (s.state === 'Failed') {
          <div class="error-banner" role="alert">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ s.errorMessage || 'Import przerwany. Szczegóły w logu serwera.' }} Istniejące pliki nie zostały uszkodzone – podmiana każdego pliku następuje dopiero po sprawdzeniu sumy kontrolnej.</span>
          </div>
        }

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon stat-icon--added"><i class="pi pi-plus-circle"></i></div>
            <div><div class="stat-label">Dodane</div><div class="stat-value">{{ s.addedCount }}</div></div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--replaced"><i class="pi pi-sync"></i></div>
            <div><div class="stat-label">Zastąpione</div><div class="stat-value">{{ s.replacedCount }}</div></div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--unchanged"><i class="pi pi-check"></i></div>
            <div><div class="stat-label">Bez zmian</div><div class="stat-value">{{ s.unchangedCount }}</div></div>
          </div>
          <div class="stat-card">
            <div class="stat-icon stat-icon--skipped"><i class="pi pi-minus-circle"></i></div>
            <div><div class="stat-label">Pominięte</div><div class="stat-value">{{ s.skippedCount }}</div></div>
          </div>
          <div class="stat-card" [class.stat-card--error]="s.failedCount > 0">
            <div class="stat-icon stat-icon--errors" [class.stat-icon--has-errors]="s.failedCount > 0"><i class="pi pi-exclamation-circle"></i></div>
            <div><div class="stat-label">Błędy</div><div class="stat-value" [class.text-red-600]="s.failedCount > 0">{{ s.failedCount }}</div></div>
          </div>
        </div>

        @if (s.state === 'Completed') {
          <div class="text-sm mt-3 index-summary" data-clarity-unmask="true">
            <i class="pi pi-database mr-1"></i>
            W indeksie mediów: <span class="font-semibold">{{ s.indexedFilesCount ?? '–' }}</span> plików.
            @if ((s.wmvAwaitingConversion ?? 0) > 0) {
              Filmy .wmv czekające na konwersję do webm/mov: <span class="font-semibold">{{ s.wmvAwaitingConversion }}</span>
              (konwersja trwa w tle; do jej końca te filmy nie odtworzą się w aplikacji).
            } @else {
              Wszystkie filmy .wmv mają już konwersje.
            }
          </div>
        }

        <div class="lists mt-3">
          @if (s.replacedFiles.length) {
            <p-panel [header]="'Zastąpione pliki (' + s.replacedCount + ')'" [toggleable]="true" [collapsed]="true" styleClass="list-panel">
              <ul class="file-list"><li *ngFor="let f of s.replacedFiles">{{ f }}</li></ul>
            </p-panel>
          }
          @if (s.addedFiles.length) {
            <p-panel [header]="'Dodane pliki (' + s.addedCount + ')'" [toggleable]="true" [collapsed]="true" styleClass="list-panel">
              <ul class="file-list"><li *ngFor="let f of s.addedFiles">{{ f }}</li></ul>
            </p-panel>
          }
          @if (s.skippedFiles.length) {
            <p-panel [header]="'Pominięte (' + s.skippedCount + ')'" [toggleable]="true" [collapsed]="true" styleClass="list-panel">
              <ul class="file-list"><li *ngFor="let f of s.skippedFiles">{{ f }}</li></ul>
            </p-panel>
          }
          @if (s.errors.length) {
            <p-panel [header]="'Błędy (' + s.failedCount + ')'" [toggleable]="true" [collapsed]="false" styleClass="list-panel list-panel--errors">
              <ul class="file-list"><li *ngFor="let e of s.errors">{{ e }}</li></ul>
            </p-panel>
          }
        </div>
      } @else {
        <p class="text-sm text-600 m-0">Od uruchomienia serwera nie przesłano żadnej paczki mediów.</p>
      }
    } @else if (!loadingStatus()) {
      <p class="text-sm text-600 m-0">Stan importu niedostępny.</p>
    }
  </div>
</div>
